import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, errorMessage } from "../api/client.ts";
import type { List, Overview as OverviewData, Payout, VaultData } from "../api/types.ts";
import { DataTable } from "../components/DataTable.tsx";
import { DateRange } from "../components/Filters.tsx";
import { FlowChart } from "../components/FlowChart.tsx";
import { IconExternal } from "../components/Icons.tsx";
import { Alert, AssetChip, ChainLogo, PageHeader } from "../components/ui.tsx";
import { fmt, plural, useT } from "../i18n/index.tsx";
import { chainName, exactUnits, formatUnits, formatUsd, shorten, timeAgo, tokenOf, utcDay } from "../lib/format.ts";
import { useAsync } from "../lib/hooks.ts";
import { useMe, useSession } from "../lib/session.tsx";

export function Overview() {
  const t = useT();
  const me = useMe();
  const { can } = useSession();
  // Today by default; "All time" clears from.
  const [range, setRange] = useState({ from: utcDay(0), to: "" });
  const { data, error, loading, reload } = useAsync(
    () => api.get<OverviewData>("/overview", { ...(range.from ? { from: range.from } : {}), ...(range.to ? { to: range.to } : {}) }),
    [range.from, range.to],
  );
  // Today's vault payout limit per asset, when the role can see the vault (skipped otherwise; errors ignored).
  const vault = useAsync(() => (can("vault:view") ? api.get<VaultData>("/vault").catch(() => null) : Promise.resolve(null)), []);
  const vaultOf = (asset: string) => vault.data?.items.find((v) => v.asset === asset);
  const first = me.user.name.split(" ")[0] ?? "";
  const org = me.organization;
  useSupportGroupPoll();

  return (
    <div className="page">
      <PageHeader
        title={fmt(t.overview.greeting, { name: first })}
        eyebrow={t.overview.eyebrow}
        actions={org.support_group_url || org.finance_group_url ? (
          <div className="tg-groups">
            <div className="tg-groups-row">
              {org.support_group_url && (
                <a href={org.support_group_url} target="_blank" rel="noreferrer noopener" className="btn btn-ghost">
                  {t.shell.tgGeneral} <IconExternal size={16} />
                </a>
              )}
              {org.finance_group_url && (
                <a href={org.finance_group_url} target="_blank" rel="noreferrer noopener" className="btn btn-ghost" aria-describedby="tg-fin-hint">
                  {t.shell.tgFinance} <IconExternal size={16} />
                </a>
              )}
            </div>
            {org.finance_group_url && <p id="tg-fin-hint" className="muted small">{t.shell.tgFinanceHint}</p>}
          </div>
        ) : undefined}
      >
        {fmt(t.overview.intro, { merchant: me.merchant.name })}
        {org.customer_number && <span className="customer-no">{fmt(t.shell.customerNo, { number: org.customer_number })}</span>}
      </PageHeader>
      <DateRange from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
      {error ? (
        <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void reload()}>{t.common.retry}</button>}>{errorMessage(error)}</Alert>
      ) : !data ? (
        <OverviewSkeleton />
      ) : (
        <>
          <section className={`card summary${loading ? " is-loading" : ""}`} aria-label={t.overview.summaryLabel}>
            <div className="summary-item summary-main">
              <div className="eyebrow">{t.overview.totalAvailable}</div>
              <div className="summary-value">{formatUsd(data.totals_usd.available)}</div>
              <div className="summary-sub">{fmt(t.overview.reservedForPending, { amount: formatUsd(data.totals_usd.pending_payout) })}</div>
            </div>
            <div className="summary-item">
              <div className="eyebrow">{t.overview.deposits}</div>
              <div className="summary-value">{formatUsd(data.period.deposits.amount_usd)}</div>
              <div className="summary-sub">{plural(data.period.deposits.count, t.overview.depositsSub, { fee: formatUsd(data.period.deposits.fee_usd) })}</div>
            </div>
            <div className="summary-item">
              <div className="eyebrow">{t.overview.payouts}</div>
              <div className="summary-value">{formatUsd(data.period.payouts.amount_usd)}</div>
              <div className="summary-sub">{plural(data.period.payouts.count, t.overview.payoutsSub, { fee: formatUsd(data.period.payouts.fee_usd) })}</div>
            </div>
          </section>

          <section className={`card summary summary-period section-block${loading ? " is-loading" : ""}`} aria-label={t.overview.periodLabel}>
            <div className="summary-item">
              <div className="eyebrow">{t.overview.swept}</div>
              <div className="summary-value">{formatUsd(data.period.swept.amount_usd)}</div>
              <div className="summary-sub">{plural(data.period.swept.count, t.overview.sweptSub)}</div>
            </div>
            <div className="summary-item">
              <div className="eyebrow">{t.overview.fees}</div>
              <div className="summary-value">{formatUsd(data.period.fees_usd)}</div>
              <div className="summary-sub">{t.overview.feesSub}</div>
            </div>
          </section>

          {data.period.by_asset.length > 1 && (
            <section className="section-block" aria-labelledby="net-h">
              <div className="section-head"><h2 id="net-h">{t.overview.byNetwork}</h2></div>
              <DataTable
                caption={t.overview.byNetwork}
                rows={data.period.by_asset}
                rowKey={(r) => r.asset}
                columns={[
                  { key: "asset", header: t.overview.network, mobile: "title", cell: (r) => <AssetChip asset={r.asset} chain={r.asset.split(":")[0]!} /> },
                  { key: "deposits", header: t.overview.deposits, align: "right", cell: (r) => <strong className="amount" title={exactUnits(r.deposits, r.decimals)}>{formatUnits(r.deposits, r.decimals)}</strong> },
                  { key: "payouts", header: t.overview.payouts, align: "right", cell: (r) => <span className="amount" title={exactUnits(r.payouts, r.decimals)}>{formatUnits(r.payouts, r.decimals)}</span> },
                  { key: "swept", header: t.overview.swept, align: "right", cell: (r) => <span className="amount" title={exactUnits(r.swept, r.decimals)}>{formatUnits(r.swept, r.decimals)}</span> },
                  { key: "fees", header: t.overview.fees, align: "right", cell: (r) => <span className="amount muted" title={exactUnits(r.fees, r.decimals)}>{formatUnits(r.fees, r.decimals)}</span> },
                  { key: "available", header: t.overview.totalAvailable, align: "right",
                    cell: (r) => {
                      const raw = data.balances.find((b) => b.asset === r.asset)?.available ?? "0";
                      return <span className="amount" title={exactUnits(raw, r.decimals)}>{formatUnits(raw, r.decimals)}</span>;
                    } },
                ]}
              />
            </section>
          )}

          {data.pending_approvals > 0 && can("payouts:view") && <WaitingForApproval count={data.pending_approvals} />}

          <section className="section-block" aria-labelledby="bal-h">
            <div className="section-head">
              <h2 id="bal-h">{t.overview.balances}</h2>
              {data.balances.length > 0 && <span className="muted small">{t.overview.stablecoinsNote}</span>}
            </div>
            {data.balances.length === 0 ? (
              <div className="card empty-card">
                <strong>{t.overview.noAssetsTitle}</strong>
                <p>{fmt(t.overview.noAssetsBody, { merchant: me.merchant.name })}</p>
              </div>
            ) : (
              <div className="balance-grid">
                {data.balances.map((b) => (
                  <div key={b.asset} className="card balance-card">
                    <div className="balance-head">
                      <ChainLogo chain={b.chain} size={32} />
                      <div>
                        <strong>{b.token}</strong>
                        <span className="chain-chip">{chainName(b.chain)}</span>
                      </div>
                    </div>
                    <div className="balance-main">
                    <div className="balance-label">{t.overview.available}</div>
                    <div className="balance-value" title={`${exactUnits(b.available, b.decimals)} ${b.token}`}>
                      {formatUnits(b.available, b.decimals)} <span>{b.token}</span>
                    </div>
                    {vaultOf(b.asset)?.per_tx_limit === "0" && <div className="balance-note warn">{t.vault.overviewOff}</div>}
                    </div>
                    <dl className="balance-rows">
                      {b.pending_sweep && b.pending_sweep !== "0" && (
                        <BalanceRow label={t.vault.pendingSweep} raw={b.pending_sweep} decimals={b.decimals} token={b.token} />
                      )}
                      {(() => {
                        const v = vaultOf(b.asset);
                        if (!v || v.per_tx_limit === "0" || v.remaining_today === null) return null;
                        return <BalanceRow label={t.vault.overviewLeftLabel} raw={v.remaining_today} decimals={b.decimals} token={b.token} />;
                      })()}
                      <BalanceRow label={t.overview.pendingPayouts} raw={b.pending_payout} decimals={b.decimals} token={b.token} />
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="card section-block chart-card">
            <FlowChart days={data.days} emptyText={t.overview.noFlow} />
          </section>
        </>
      )}
    </div>
  );
}

/** The oldest payouts waiting for approval, with a Review link to each drawer. */
function BalanceRow({ label, raw, decimals, token }: { label: string; raw: string; decimals: number; token: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd title={`${exactUnits(raw, decimals)} ${token}`}>{formatUnits(raw, decimals)} {token}</dd>
    </div>
  );
}

function WaitingForApproval({ count }: { count: number }) {
  const t = useT();
  const { can } = useSession();
  const navigate = useNavigate();
  const q = useAsync(() => api.get<List<Payout>>("/payouts", { status: "pending_approval", limit: 100 }), []);
  const oldest = [...(q.data?.items ?? [])].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at)).slice(0, 3);
  const total = Math.max(count, q.data?.items.length ?? 0);

  return (
    <section className="card section-block approvals" aria-labelledby="appr-h">
      <div className="approvals-head">
        <h2 id="appr-h">{t.overview.waitingForApproval} <span className="count-pill" aria-label={plural(total, t.overview.payoutsCount)}>{total}</span></h2>
        <span className="approvals-sub">{can("payouts:approve") ? t.overview.oldestFirst : t.overview.approverHint}</span>
      </div>
      {q.error ? (
        <div className="approvals-foot"><span className="muted small">{errorMessage(q.error)}</span></div>
      ) : (
        <ul className="approval-rows">
          {q.loading && !q.data
            ? [0, 1].map((i) => <li key={i} className="approval-row"><span className="sk" /><span className="sk" /><span className="sk sk-sm" /><span className="sk sk-sm" /><span /></li>)
            : oldest.map((p) => (
                <li key={p.id} className="approval-row">
                  <span className="approval-amount" title={exactUnits(p.amount, p.decimals)}>{formatUnits(p.amount, p.decimals)} <span>{tokenOf(p.asset)}</span></span>
                  <span className="mono-sm" title={p.to}>{fmt(t.overview.toAddress, { address: shorten(p.to) })}</span>
                  <AssetChip asset={p.asset} chain={p.chain} />
                  <span className="approval-who">
                    <span>{p.created_by ?? "API"}</span>
                    <span title={new Date(p.created_at).toISOString()}>{timeAgo(p.created_at)}</span>
                  </span>
                  <button type="button" className="link-btn" onClick={() => navigate(`/payouts?status=pending_approval&open=${encodeURIComponent(p.id)}`)}>
                    {t.common.review}
                  </button>
                </li>
              ))}
        </ul>
      )}
      <div className="approvals-foot">
        <Link to="/payouts?status=pending_approval">{fmt(t.overview.seeAll, { n: total })}</Link>
      </div>
    </section>
  );
}

function OverviewSkeleton() {
  const t = useT();
  return (
    <div aria-busy="true" aria-label={t.overview.loadingOverview}>
      <div className="card summary">
        {[0, 1, 2].map((i) => (
          <div key={i} className="summary-item"><span className="sk sk-sm" /><span className="sk sk-lg" /><span className="sk" /></div>
        ))}
      </div>
      <div className="balance-grid section-block">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card balance-card"><span className="sk sk-sm" /><span className="sk sk-lg" /><span className="sk" /></div>
        ))}
      </div>
    </div>
  );
}

/**
 * Right after sign-up the Telegram support group is created a few seconds after the e-mail is confirmed: while the brand has a
 * customer number but no support group yet, re-read the session every 10 s (for up to 2 minutes) so the button appears.
 */
function useSupportGroupPoll() {
  const me = useMe();
  const { refresh } = useSession();
  const org = me.organization;
  const waiting = !!org.customer_number && !!me.merchant.in_review && !org.support_group_url;
  useEffect(() => {
    if (!waiting) return;
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      if (n > 12) return window.clearInterval(id);
      refresh().catch(() => { /* ignore: next tick or a reload will pick it up */ });
    }, 10_000);
    return () => window.clearInterval(id);
  }, [waiting, refresh]);
}
